import {
  getReceiptSystemAccountIdSafe,
  native,
  type Product,
  type SubscriptionOffer,
} from '@forest/native-services'
import {
  AbortError,
  classifyPurchaseError,
  createSubscribeFlow,
  ensureAuthenticatedUser,
  failureCause,
  withFailure,
  type Failure,
  type PaymentPlatform,
  type PurchaseErrorIntent,
  type ServiceLevelStatus,
  type SubscriptionServiceLevelStatus,
} from '@forest/paywall-core'
import { dialog } from '@forest/shared/solid'
import { appVersion } from '~/lib/params'
import { subscriptionRecord } from '~/lib/service-level'
import { m } from '~/paraglide/messages'
import { api, userInfoSource } from '~/services/api'

/**
 * Resubscribe confirmation (GLOSSARY.md, ADR 0035): the purchase is confirmed
 * once a subscription's `expires_at` moves off the pre-purchase record's
 * (`baseline`), or auto-renew is on for the purchased SKU. With no record
 * before the purchase, any subscription confirms. The launch never admits a
 * record with auto-renew on, so that clause cannot confirm a stale one.
 */
function isResubscribeConfirmation(
  status: ServiceLevelStatus,
  purchasedSku: string,
  baseline: number | null,
): status is SubscriptionServiceLevelStatus {
  const record = subscriptionRecord(status)
  if (!record) return false
  if (baseline === null) return true
  const { expires_at, is_auto_renewing, auto_renew_sku_id } = record
  const expiresAtMs = Date.parse(expires_at)
  return (
    (Number.isFinite(expiresAtMs) && expiresAtMs !== baseline) ||
    (is_auto_renewing && auto_renew_sku_id === purchasedSku)
  )
}

/**
 * The abandoned checkout (GLOSSARY.md): the user backed out of the store
 * payment sheet. Silent here too — the plan picker simply stays.
 */
function isAbandonedCheckout(
  intent: PurchaseErrorIntent,
  platform: PaymentPlatform,
) {
  return (
    intent.kind === 'payment_timeout' &&
    intent.detail === 'interrupted' &&
    platform !== 'china_android'
  )
}

/** Asks before a log-in; dismissing it declines, which is silent. */
function confirmLogIn(title: string, description: string) {
  return new Promise<void>((resolve, reject) => {
    void dialog.open({
      title,
      description,
      button: m.OK(),
      action: resolve,
      closeButton: { action: () => reject(new AbortError()) },
      // Escape would dismiss past both buttons and leave the prompt pending.
      closeOnEscapeKeyDown: false,
    })
  })
}

async function getAuthenticatedUserId(): Promise<number> {
  const userInfo = await ensureAuthenticatedUser({
    getUserInfo: async () => {
      const result = await native.user.getUserInfo()
      if (!result.success) throw new Error('getUserInfo failed')
      if (!result.data) throw new Error('getUserInfo returned null data')
      return { success: true as const, data: result.data }
    },
    auth: () => native.auth.auth(),
    bindLegacyUser: () => native.auth.bindLegacyUser(),
    onBeforeGuestUpgrade: () =>
      confirmLogIn(m.dialog_log_in_title(), m.dialog_log_in_content()),
    onBeforeLegacyBind: () =>
      confirmLogIn(
        m.dialog_log_in_legacy_title(),
        m.dialog_log_in_legacy_content(),
      ),
    onUserInfo: (userInfo) => userInfoSource.atom.set(userInfo),
  })
  return userInfo.user_id
}

function showUnknownErrorDialog(failure: Failure) {
  void dialog.open({
    title: m.dialog_unknown_error_title(),
    description: withFailure(
      m.dialog_unknown_error_content({ error_code: failure.code }),
      failure,
      { codeInDescription: true },
    ),
    button: m.OK(),
  })
}

/**
 * A subscription conflict: another Forest account owns the subscription. Names
 * that account, or — when it is deleted or unknown — sends the user to manage
 * the store subscription. The same dialogs Restore Purchases shows elsewhere.
 */
async function showSubscriptionConflictDialog(
  ownerUid: number | null,
  failure: Failure,
) {
  const showDeletedAccountDialog = () =>
    void dialog.open({
      title: m.dialog_subscription_link_delete_account_title(),
      description: withFailure(
        m.dialog_subscription_link_delete_account_content(),
        failure,
      ),
      button: m.btn_subscription_management(),
      action: () =>
        void native.general.openURL(
          'forestapp://instant/system_subscription_management',
        ),
    })
  if (ownerUid === null) {
    showDeletedAccountDialog()
    return
  }
  let userName = ''
  let userEmail = ''
  try {
    const info = await api.service
      .get(`user/users/${ownerUid}/info`)
      .json<{ name: string; email: string }>()
    userName = info.name
    userEmail = info.email
  } catch (lookupError) {
    if (failureCause(lookupError) === 'http_404') {
      showDeletedAccountDialog()
      return
    }
  }
  void dialog.open({
    title: m.dialog_subscription_conflict_title(),
    description: withFailure(
      m.dialog_subscription_conflict_content({
        user_name: userName,
        user_account: userEmail,
      }),
      failure,
    ),
    button: m.OK(),
  })
}

/**
 * The purchase confirmation dialog (GLOSSARY.md): the payment may have
 * succeeded but is not yet confirmed. Confirming it closes the page, so the
 * user cannot pay again while the first payment may still complete.
 */
function showPurchaseConfirmationDialog(failure: Failure) {
  void dialog.open({
    title: m.dialog_confirming_perchase_title(),
    description: withFailure(
      m.dialog_confirming_perchase_description(),
      failure,
    ),
    button: m.OK(),
    action: () => {
      void native.user.refreshServiceLevel()
      void native.general.close()
    },
  })
}

function showPurchaseErrorDialog(intent: PurchaseErrorIntent) {
  switch (intent.kind) {
    case 'payment_timeout':
      void dialog.open({
        title: m.dialog_payment_timeout_title(),
        description: withFailure(
          m.dialog_payment_timeout_content({ error: intent.detail }),
          intent.failure,
        ),
        button: m.OK(),
      })
      return
    case 'no_internet':
      void dialog.open({
        title: m.dialog_no_internet_title(),
        description: withFailure(
          m.dialog_no_internet_content(),
          intent.failure,
        ),
        button: m.OK(),
      })
      return
    case 'confirmation_pending':
      showPurchaseConfirmationDialog(intent.failure)
      return
    case 'subscription_conflict':
      void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
      return
    case 'http_error':
    case 'unknown':
      showUnknownErrorDialog(intent.failure)
      return
    // Restore purchases is out of scope for this page (#455), so a payment
    // whose receipt did not link gets the general dialog, not the restore one.
    // Its failure carries the store transaction ID for support.
    case 'illegal_transaction_restore':
      showUnknownErrorDialog(intent.failure)
      return
    default:
      intent satisfies never
  }
}

/**
 * Renewal resumed (GLOSSARY.md): the permanent paywall shows the buyer their
 * now-renewing subscription over this page, which closes once they leave it.
 * The bridge keeps `openPaywall` pending for as long as that paywall is up.
 */
async function resumeRenewal() {
  try {
    await native.paywall.openPaywall('permanent', 'plus_countdown')
  } catch {
    // Native owns any failure to open it; the purchase itself succeeded.
  }
  void native.general.close()
}

/**
 * Buys a plan, confirmed by resubscribe confirmation against the
 * pre-purchase record's expiry, through native's service level (ADR 0035).
 * Resolves once the attempt has played out: on success, the permanent
 * paywall left for an expiring subscriber (renewal resumed) or the success
 * view handed to `onPlusRegained` for a lapsed one (Plus regained); silence
 * on an abandoned checkout or a declined log-in; the error dialog otherwise.
 */
export function createPurchase(options: {
  platform: PaymentPlatform
  confirmationBaseline: number | null
  isExpiringSubscriber: boolean
  onPlusRegained: () => void
}) {
  const { platform, confirmationBaseline, isExpiringSubscriber } = options
  // Read after authenticating, so it belongs to the account that buys.
  let receiptSystemAccountId: string | null = null

  const flow = createSubscribeFlow<Product>({
    api,
    platform,
    native: {
      purchase: native.iap.purchase,
      closeTransaction: native.iap.closeTransaction,
      getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
      getAllEntitlements: native.iap.getAllEntitlements,
      logEvent: native.general.logEvent,
      refreshServiceLevel: () => native.user.refreshServiceLevel(),
    },
    getUserId: async () => {
      const userId = await getAuthenticatedUserId()
      receiptSystemAccountId = await getReceiptSystemAccountIdSafe(appVersion)
      return userId
    },
    getReceiptSystemAccountId: () => receiptSystemAccountId,
    shouldResolve: (status, purchasedSku) =>
      isResubscribeConfirmation(status, purchasedSku, confirmationBaseline),
    fetchServiceLevelStatus: () => native.user.refreshServiceLevel(),
  })

  /** `offer` is the promotional offer to apply. */
  return async function purchase(product: Product, offer?: SubscriptionOffer) {
    try {
      // A `null` status is the confirmation deadline passing: the receipt was
      // accepted, so the purchase still counts as successful (ADR 0035).
      const status = await flow.run(product, { offer })
      flow.handleSuccess(status, product)
    } catch (error) {
      if (error instanceof AbortError) return
      const intent = classifyPurchaseError(error)
      if (isAbandonedCheckout(intent, platform)) return
      showPurchaseErrorDialog(intent)
      return
    }
    if (isExpiringSubscriber) await resumeRenewal()
    else options.onPlusRegained()
  }
}
